/**
 * The project a canvas card resolves against.
 *
 * `setCanvasCallbacks({ renderCard })` is a module-level slot, and the card
 * component it holds is rendered inside the board's own React root -- the one
 * `mountExtensionEditor` creates. No context from the console's tree reaches it,
 * so the org, the project and the team credentials a `doc` card needs cannot be
 * props and cannot be a provider. They are published here instead, by the same
 * effect that starts and disposes the Shared Docs scope.
 *
 * Scoped to the project rather than to the board on purpose. A card names an
 * org and a document id and never a project (see `CanvasDocumentTarget`), and
 * the console has exactly one project scope open at a time -- which is the same
 * fact `WebCollabHost` is built around. A board's cards therefore resolve in the
 * project the board is open in, and a card whose org disagrees is refused rather
 * than routed through the wrong room.
 */

import { useSyncExternalStore } from 'react';

import type { TeamJwt, TeamSession } from '../auth/teamJwt';

export interface WebCanvasCardScope {
  orgId: string;
  projectId: string;
  teamSession: TeamSession;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
}

let activeScope: WebCanvasCardScope | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function getWebCanvasCardScope(): WebCanvasCardScope | null {
  return activeScope;
}

export function setWebCanvasCardScope(scope: WebCanvasCardScope): void {
  if (activeScope === scope) return;
  activeScope = scope;
  notify();
}

/**
 * Clear, but only if `scope` is still the published one. A project switch runs
 * the incoming scope's effect before the outgoing one's cleanup, and an
 * unconditional clear there would leave the console with no scope at all.
 */
export function clearWebCanvasCardScope(scope: WebCanvasCardScope): void {
  if (activeScope !== scope) return;
  activeScope = null;
  notify();
}

/**
 * Subscribed rather than read once.
 *
 * Effects run child-first, so a board can mount before the docs scope that owns
 * it has published anything. Read once, that gap is permanent for the life of
 * the mount and every `doc` card on the board sits on "no shared project is
 * open" forever. The scope object is stable per project, so it is its own
 * snapshot.
 */
export function useWebCanvasCardScope(): WebCanvasCardScope | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getWebCanvasCardScope,
    getWebCanvasCardScope,
  );
}
